import { LocalDate } from "@js-joda/core";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getStockholmLocalDateNow } from "components/designV2/datePicker/datePicker.utils";
import { CustomIcon } from "components/icon/customIcon.component";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import type { StaffRegisterItem } from "services/api/company/timeReports.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useTimeReportsV2Store } from "web-apps/company/stores/timeReportsV2/timeReportsV2Store";
import type { TimeReportsV2StaffRegisterQuery } from "web-apps/company/stores/timeReportsV2/timeReportsV2Store.reducer";
import {
  formatLocalDateAsApiDate,
  formatTimeLabel,
  formatTimeRangeLabel,
} from "web-apps/company/utils/timeAndDate";
import {
  Cell,
  CellLabel,
  CellValue,
  LoadMoreSentinel,
  Message,
  Person,
  PersonMeta,
  PersonName,
  Row,
  Rows,
  SectionCard,
  SectionHeader,
  SectionSubTitle,
  SectionTitle,
  StatusPill,
  Wrapper,
} from "./staffRegisterView.styled";
import {
  toStaffRegisterStatusIconColor,
  toStaffRegisterStatusIconName,
  toStaffRegisterStatusText,
  toStaffRegisterStatusTone,
} from "./staffRegisterView.status.mapper";

const PAGE_SIZE = 50;

interface StaffRegisterViewProps {
  readonly selectedDate: LocalDate;
}

interface StaffRegisterSections {
  readonly history: readonly StaffRegisterItem[];
  readonly notCheckedIn: readonly StaffRegisterItem[];
  readonly onSiteNow: readonly StaffRegisterItem[];
}

const groupStaffRegisterItems = (
  items: readonly StaffRegisterItem[],
): StaffRegisterSections => {
  const onSiteNow: StaffRegisterItem[] = [];
  const notCheckedIn: StaffRegisterItem[] = [];
  const history: StaffRegisterItem[] = [];

  items.forEach((item) => {
    if (item.status === "New") {
      notCheckedIn.push(item);
      return;
    }

    if (item.status === "Started") {
      onSiteNow.push(item);
      return;
    }

    if (item.status === "Stamped" || item.status === "TimeReported") {
      history.push(item);
      return;
    }

    // Keep unknown/legacy states visible in history instead of dropping rows.
    history.push(item);
  });

  return { onSiteNow, notCheckedIn, history };
};

const resolveStampedStart = (item: StaffRegisterItem): string | null =>
  item.stampedStartTime ?? null;

const resolveStampedEnd = (item: StaffRegisterItem): string | null =>
  item.stampedEndTime ?? null;

const toStaffRegisterRowKey = (
  item: StaffRegisterItem,
  section: "onSiteNow" | "notCheckedIn" | "history",
  rowIndex: number,
): string => {
  return `${item.id}-${section}-${item.plannedStartTime}-${rowIndex}`;
};

const toTimeColumns = (
  item: StaffRegisterItem,
  locale: string,
  translate: (key: string) => string,
  section: "onSiteNow" | "notCheckedIn" | "history",
): {
  leftValue: string;
  rightValue: string;
  leftLabel: string;
  rightLabel: string;
} => {
  const normalizeTimeLabel = (value: string): string =>
    value === "--" ? "-" : value;

  const toStampedValue = (value: string | null): string => {
    const formatted = formatTimeLabel(value, locale);
    return normalizeTimeLabel(formatted);
  };

  if (section === "onSiteNow") {
    return {
      leftValue: toStampedValue(resolveStampedStart(item)),
      rightValue: toStampedValue(resolveStampedEnd(item)),
      leftLabel: translate("TimeReports.StampedIn"),
      rightLabel: translate("TimeReports.StampedOut"),
    };
  }

  if (section === "history") {
    const stampedStart = resolveStampedStart(item);
    const stampedEnd = resolveStampedEnd(item);
    const historyStart = stampedStart ?? item.reportedStartTime ?? null;
    const historyEnd = stampedEnd ?? item.reportedEndTime ?? null;
    const isReportedStartFallback =
      stampedStart == null && item.reportedStartTime != null;
    const isReportedEndFallback =
      stampedEnd == null && item.reportedEndTime != null;
    return {
      leftValue: toStampedValue(historyStart),
      rightValue: toStampedValue(historyEnd),
      leftLabel: isReportedStartFallback
        ? translate("TimeReports.Reported")
        : translate("TimeReports.StampedIn"),
      rightLabel: isReportedEndFallback
        ? translate("TimeReports.Reported")
        : translate("TimeReports.StampedOut"),
    };
  }

  return {
    leftValue: normalizeTimeLabel(
      formatTimeRangeLabel(item.plannedStartTime, item.plannedEndTime, locale),
    ),
    rightValue: "",
    leftLabel: translate("TimeReports.Scheduled"),
    rightLabel: "",
  };
};

export const StaffRegisterView = ({
  selectedDate,
}: StaffRegisterViewProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [companyState] = useCompanyStore();
  const [timeReportsV2State, timeReportsV2Actions] = useTimeReportsV2Store();
  const companyId = companyState.company?.id;
  const staffRegisterQuery = useMemo<TimeReportsV2StaffRegisterQuery>(() => {
    const dayKey = formatLocalDateAsApiDate(selectedDate);
    return { fromDate: dayKey, pageSize: PAGE_SIZE, toDate: dayKey };
  }, [selectedDate]);

  useEffect(() => {
    if (!companyId) return;
    void timeReportsV2Actions.fetchStaffRegister({
      append: false,
      companyId,
      page: 0,
      query: staffRegisterQuery,
    });
  }, [companyId, staffRegisterQuery, timeReportsV2Actions]);

  const staffRegisterItems = useMemo(
    () =>
      companyId != null ? (timeReportsV2State.staffRegister?.items ?? []) : [],
    [companyId, timeReportsV2State.staffRegister],
  );

  const sections = useMemo<StaffRegisterSections>(() => {
    return groupStaffRegisterItems(staffRegisterItems);
  }, [staffRegisterItems]);
  const isStaffRegisterLoading =
    companyId != null && timeReportsV2State.isStaffRegisterLoading;
  const isStaffRegisterLoadingMore =
    companyId != null && timeReportsV2State.isStaffRegisterLoadingMore;
  const hasStaffRegisterError =
    companyId != null && timeReportsV2State.staffRegisterError != null;
  const hasMore = timeReportsV2State.staffRegisterHasMore;
  const isPastDate = selectedDate.isBefore(getStockholmLocalDateNow());

  const handleLoadMore = useCallback((): void => {
    if (!companyId || !hasMore || isStaffRegisterLoading) return;
    if (isStaffRegisterLoadingMore) return;

    void timeReportsV2Actions.fetchStaffRegister({
      append: true,
      companyId,
      page: timeReportsV2State.staffRegisterPage + 1,
      query: staffRegisterQuery,
    });
  }, [
    companyId,
    hasMore,
    isStaffRegisterLoading,
    isStaffRegisterLoadingMore,
    staffRegisterQuery,
    timeReportsV2Actions,
    timeReportsV2State.staffRegisterPage,
  ]);

  const { loadMoreRef } = useInfiniteScroll({
    hasMore: hasMore && !hasStaffRegisterError,
    isLoading: isStaffRegisterLoading || isStaffRegisterLoadingMore,
    itemCount: staffRegisterItems.length,
    onLoadMore: handleLoadMore,
  });

  const renderRows = (
    items: readonly StaffRegisterItem[],
    section: "onSiteNow" | "notCheckedIn" | "history",
  ): JSX.Element => (
    <Rows>
      {items.map((item, rowIndex) => {
        const columns = toTimeColumns(item, i18n.language, t, section);
        const isMissingCheckedOutValue =
          section === "onSiteNow" && columns.rightValue === "-";
        const statusValue = item.status;
        const statusText = toStaffRegisterStatusText(statusValue, t);
        const statusIconName = toStaffRegisterStatusIconName(statusValue);
        const rowKey = toStaffRegisterRowKey(item, section, rowIndex);

        return (
          <Row key={rowKey}>
            <Person>
              <PersonName>
                {item.worker.firstName} {item.worker.lastName}
              </PersonName>
              <PersonMeta>
                {t("TimeReports.PersonalNumber")}{" "}
                {item.worker.personalIdentityNumber ?? "-"}
              </PersonMeta>
            </Person>
            <Cell>
              <CellValue>{columns.leftValue}</CellValue>
              <CellLabel>{columns.leftLabel}</CellLabel>
            </Cell>
            <Cell>
              <CellValue $muted={isMissingCheckedOutValue}>
                {columns.rightValue}
              </CellValue>
              <CellLabel $muted={isMissingCheckedOutValue}>
                {columns.rightLabel}
              </CellLabel>
            </Cell>
            <StatusPill $tone={toStaffRegisterStatusTone(statusValue)}>
              {statusIconName && (
                <CustomIcon
                  name={statusIconName}
                  size="16px"
                  color={toStaffRegisterStatusIconColor(statusValue)}
                />
              )}
              {statusText}
            </StatusPill>
          </Row>
        );
      })}
    </Rows>
  );

  return (
    <Wrapper aria-busy={isStaffRegisterLoading || isStaffRegisterLoadingMore}>
      {isStaffRegisterLoading ? (
        <Message>{t("TimeReports.LoadingStaffRegister")}</Message>
      ) : hasStaffRegisterError && staffRegisterItems.length === 0 ? (
        <Message>{t("TimeReports.LoadStaffRegisterError")}</Message>
      ) : (
        <>
          {!isPastDate ? (
            <>
              <SectionCard>
                <SectionHeader>
                  <SectionTitle>{t("TimeReports.OnSiteNow")}</SectionTitle>
                  <SectionSubTitle>
                    {t("TimeReports.PeopleOnSite", {
                      count: sections.onSiteNow.length,
                    })}
                  </SectionSubTitle>
                </SectionHeader>
                {sections.onSiteNow.length > 0 ? (
                  renderRows(sections.onSiteNow, "onSiteNow")
                ) : (
                  <Message>{t("TimeReports.NoRows")}</Message>
                )}
              </SectionCard>

              <SectionCard>
                <SectionHeader>
                  <SectionTitle>
                    {t("TimeReports.NotCheckedInSection")}
                  </SectionTitle>
                  <SectionSubTitle>
                    {t("TimeReports.PeopleNotCheckedIn", {
                      count: sections.notCheckedIn.length,
                    })}
                  </SectionSubTitle>
                </SectionHeader>
                {sections.notCheckedIn.length > 0 ? (
                  renderRows(sections.notCheckedIn, "notCheckedIn")
                ) : (
                  <Message>{t("TimeReports.NoRows")}</Message>
                )}
              </SectionCard>
            </>
          ) : null}

          <SectionCard>
            <SectionHeader>
              <SectionTitle>{t("TimeReports.TodayHistory")}</SectionTitle>
              <SectionSubTitle>
                {t("TimeReports.CompletedShifts")}
              </SectionSubTitle>
            </SectionHeader>
            {sections.history.length > 0 ? (
              renderRows(sections.history, "history")
            ) : (
              <Message>{t("TimeReports.NoRows")}</Message>
            )}
          </SectionCard>

          {isStaffRegisterLoadingMore && (
            <Message>{t("TimeReports.LoadingStaffRegister")}</Message>
          )}
          {hasStaffRegisterError && staffRegisterItems.length > 0 && (
            <Message>{t("TimeReports.LoadStaffRegisterError")}</Message>
          )}
          {hasMore && !hasStaffRegisterError && (
            <LoadMoreSentinel ref={loadMoreRef} />
          )}
        </>
      )}
    </Wrapper>
  );
};
